<template v-if="productStore.product">
  <div class="mb-6 mt-8 flex flex-col">
    <UiText type="p" size="sm">{{ $t('product.quantity') }}</UiText>
    <div class="mb-3 mt-1 flex">
      <UiInputQuantity id="product" class="mr-3" :start="1" :disabled="!!isLoading" @update-quantity="updateQuantity" />
      <UiButton
        id="product-add-to-cart"
        variant="tertiary"
        class="grow border-neutral-200 shadow-none hover:!shadow-none focus:!shadow-none"
        :loading="isLoading === 'cart'"
        :disabled="!!isLoading"
        @click="addToCart"
      >
        {{ $t('product.addToCart') }}
      </UiButton>
    </div>
    <UiButton id="product-buy-now" variant="primary" :disabled="!!isLoading" class="md:w-full">
      {{ $t('product.buyNow') }}
    </UiButton>
  </div>

  <div class="mb-6 flex flex-col gap-2 xl:flex-row">
    <UiTooltip id="product-add-to-wishlist" :label="!authStore.isLoggedIn ? $t('wishlist.loginRequired') : ''">
      <UiButton
        id="product-add-to-wishlist"
        variant="navigation"
        type="neutral"
        class="whitespace-nowrap font-normal"
        :icon="IconHeart"
        icon-side="left"
        :loading="isLoading === 'wishlist'"
        :disabled="!authStore.isLoggedIn || !!isLoading || isInWishlist"
        @click="addToWishlist"
      >
        {{ isInWishlist ? $t('product.addedToWishlist') : $t('product.addToWishlist') }}
      </UiButton>
    </UiTooltip>
    <UiButton
      id="product-compare"
      variant="navigation"
      type="neutral"
      class="whitespace-nowrap font-normal"
      :icon="IconCompare"
      icon-side="left"
      disabled
    >
      {{ $t('product.compare') }}
    </UiButton>
    <UiButton
      id="product-ask-question"
      variant="navigation"
      type="neutral"
      class="whitespace-nowrap font-normal"
      :icon="IconQuestion"
      icon-side="left"
      :disabled="!!isLoading"
      @click="goTo(`mailto:${runtimeConfig.public.supportEmail}`, { external: true })"
    >
      {{ $t('product.askQuestion') }}
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { IconCompare, IconHeart, IconQuestion } from '#components';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useAuthStore } from '~/stores/auth/authStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';
import { useProductStore } from '~/stores/product/productStore';
import { WishlistActionType } from '~/stores/wishlist/types';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';

const goTo = useGoTo();
const { locale } = useI18n();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();
const runtimeConfig = useRuntimeConfig();

const alertStore = useAlertStore();
const cartStore = useCartStore();
const productStore = useProductStore();
const appStore = useAppStore();
const wishlistStore = useWishlistStore();
const authStore = useAuthStore();

const isLoading = ref<'cart' | 'wishlist' | null>(null);
const currentQty = ref<number>(1);

const currentSku = computed(() => productStore.selectedVariantSku ?? productStore.product?.sku);

const isInWishlist = computed(
  () => !!wishlistStore.getWishlistLineItems?.find((item) => item.sku === currentSku.value),
);

const updateQuantity = (value: number): void => {
  currentQty.value = value;
};

const addToCart = (): void => {
  if (!productStore.product) {
    return;
  }

  dataLayer.push({
    component: 'product_purchase',
    event: 'button_click',
    product: productStore.product.name,
    value: 'add_to_cart',
  });

  isLoading.value = 'cart';

  cartStore
    .updateCart({
      actions: [
        {
          quantity: currentQty.value,
          sku: currentSku.value ?? productStore.product.sku,
          type: CartActionType.ADD_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'product.addedToCartSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = null));
};

const addToWishlist = (): void => {
  if (!productStore.product) {
    return;
  }

  dataLayer.push({
    component: 'product_purchase',
    event: 'button_click',
    product: productStore.product.name,
    value: 'add_to_wishlist',
  });

  isLoading.value = 'wishlist';

  wishlistStore
    .updateWishlist({
      actions: [
        {
          sku: currentSku.value ?? productStore.product.sku,
          type: WishlistActionType.ADD_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'product.addedToWishlistSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = null));
};
</script>
